/**
 * Sprint 141 — **Cuánto se cobra en la puerta.** Una sola cuenta, para los cuatro sitios que la dicen.
 *
 * ## El defecto que lo obliga a existir
 *
 * Hasta el Sprint 140, un domicilio sólo podía estar pagado del todo o no estar pagado: por eso
 * cuatro lugares distintos —la tarjeta del tablero, la ficha térmica, el HTML y el PDF— podían
 * decidir por su cuenta mirando `estadoPago` y `total`, y todos coincidían.
 *
 * El Sprint 141 rompió esa suposición sin avisarles: al que ya pagó se le puede agregar comida, y
 * entonces existe un pedido **pagado a medias**. Doña Chayo transfirió $195, pidió un lomo más y
 * quedó debiendo $145. Probado en local el 2026-09-19, esto es lo que decía cada pantalla:
 *
 * | Dónde | Qué decía | Qué pasa en la puerta |
 * |---|---|---|
 * | Tarjeta del tablero | «Se cobra en la puerta: **$340**» | el repartidor cobra **$195 de más** |
 * | Ficha que se lleva la moto | «**PAGADO — NO COBRAR NADA**» | el negocio **pierde $145** |
 *
 * Dos pantallas del mismo pedido, dos cifras equivocadas en direcciones opuestas, y la segunda es
 * la que se imprime. Por eso la cuenta se hace **una vez** y aquí: mientras cada formato la repita
 * por su cuenta, arreglar tres y olvidar el cuarto es cuestión de tiempo.
 *
 * Vive en `shared/` a propósito: la usan el servidor (los tres formatos del papel) y el navegador
 * (el tablero). Si mañana aparece un quinto sitio que diga un monto a cobrar, que lo pida aquí.
 */

export interface DatosDelCobro {
  /** Total del pedido en centavos. Incluye el envío salvo que lo cobre el repartidor aparte. */
  total: number;
  costoEnvio: number;
  /** `true` = el envío no está en `total`; lo cobra el repartidor por separado. */
  envioPagaCliente: boolean;
  /** Suma de los `Pago` registrados, en centavos. */
  pagadoRegistrado: number;
  /** ¿Existe al menos una fila de `Pago`? Distingue "no ha pagado" de "pagó antes del Sprint 70". */
  tienePagos: boolean;
  /** `Pedido.estadoPago`. Sólo se usa para los pedidos viejos, que no tienen filas de `Pago`. */
  estadoPago?: string | null;
  /** Se acordó cobrarlo al entregar. */
  pagaAlEntregar?: boolean | null;
  /** Con cuánto dijo el cliente que va a pagar, para llevar el cambio contado. */
  pagaCon?: number | null;
}

export interface CobroEnLaPuerta {
  /** Lo que el repartidor tiene que cobrar. `0` = no cobra nada. */
  falta: number;
  /** Lo que el cliente ya dejó pagado antes de que saliera la moto. */
  yaPago: number;
  /** `true` = no hay nada que cobrar en la puerta. */
  estaCubierto: boolean;
  /** `true` = pagó una parte y debe el resto. Es el caso que nació en el Sprint 141. */
  pagoParcial: boolean;
  cambio: { pagaCon: number; cambio: number } | null;
}

/**
 * Lo que de verdad se debe, descontando lo que ya entró.
 *
 * **La regla de los pedidos viejos.** La v1.0 marcaba `estadoPago='pagado'` sin crear ninguna fila
 * de `Pago` (misma historia que arregló el Sprint 70 en los reportes). Si se restara a ciegas, cada
 * uno de esos pedidos saldría hoy con una ficha que dice «COBRAR $1,035» sobre comida ya pagada, y
 * el repartidor la cobraría dos veces. Por eso: **sin filas de pago, manda `estadoPago`**; con
 * filas, manda la suma, que es el dinero que de verdad se registró.
 */
export function calcularCobroEnLaPuerta(d: DatosDelCobro): CobroEnLaPuerta {
  const envioAparte = d.envioPagaCliente && d.costoEnvio > 0;

  const yaPago = d.tienePagos
    ? d.pagadoRegistrado
    : d.estadoPago === 'pagado'
      ? d.total
      : 0;

  const faltaComida = Math.max(0, d.total - yaPago);
  const falta = faltaComida + (envioAparte ? d.costoEnvio : 0);

  /*
    El cambio se calcula contra lo que se va a cobrar, no contra el total.

    Con `pagaCon` capturado contra el total original, la cuenta correcta da **más** cambio: si
    dijo "pago con $500" sobre $340 y ya transfirió $195, sólo debe $145 y hay que devolverle
    $355, no $160. Restar del total le daría de menos al cliente en la puerta, que es donde nadie
    puede corregirlo. Y a la inversa, la moto tiene que salir con ese efectivo contado: por eso
    el número que se imprime y el que se cobra salen de la misma cuenta.
  */
  const cambio =
    falta > 0 && d.pagaAlEntregar && d.pagaCon != null && d.pagaCon > falta
      ? { pagaCon: d.pagaCon, cambio: d.pagaCon - falta }
      : null;

  return {
    falta,
    yaPago,
    estaCubierto: falta === 0,
    pagoParcial: yaPago > 0 && faltaComida > 0,
    cambio,
  };
}

export interface BannerDeLaFicha {
  principal: string;
  secundario: string | null;
  /** `true` = el banner es verde ("no cobrar"), `false` = rojo ("cobrar esto"). */
  pagado: boolean;
}

/**
 * El cartel grande de la ficha del repartidor, en las palabras que se leen en la puerta.
 *
 * Recibe el formateador de moneda del negocio porque los tres formatos ya tienen el suyo (el
 * símbolo y el locale son configurables por tenant) y no vale la pena tener un cuarto.
 */
export function bannerDeLaFicha(
  d: DatosDelCobro,
  fmt: (centavos: number) => string,
): BannerDeLaFicha {
  const cobro = calcularCobroEnLaPuerta(d);
  const envioAparte = d.envioPagaCliente && d.costoEnvio > 0;

  if (cobro.estaCubierto) {
    return { principal: 'PAGADO — NO COBRAR NADA', secundario: null, pagado: true };
  }

  // Los alimentos ya están cubiertos y lo único pendiente es el viaje.
  if (cobro.falta === d.costoEnvio && envioAparte && d.total - cobro.yaPago <= 0) {
    return {
      principal: `COBRAR ENVÍO: ${fmt(d.costoEnvio)}`,
      secundario: 'Los alimentos ya están pagados',
      pagado: true,
    };
  }

  /*
    Sprint 141 — El caso nuevo, y el que más cuidado necesita: **pagó una parte**.

    Se dice de las dos maneras a la vez —cuánto cobrar arriba, y por qué es menos que el total
    abajo— porque el repartidor va a comparar ese número con el ticket que lleva en la otra mano.
    Si sólo dijera "COBRAR $145" sobre un pedido de $340, lo natural es pensar que el papel está
    mal y cobrar lo que dice el ticket.
  */
  if (cobro.pagoParcial) {
    return {
      principal: `COBRAR ${fmt(cobro.falta)}`,
      secundario: `Ya pagó ${fmt(cobro.yaPago)} de ${fmt(d.total)}${
        envioAparte ? ` · más envío ${fmt(d.costoEnvio)}` : ''
      }`,
      pagado: false,
    };
  }

  if (envioAparte) {
    return {
      principal: `COBRAR ${fmt(d.total + d.costoEnvio)}`,
      secundario: `Alimentos ${fmt(d.total)} + Envío ${fmt(d.costoEnvio)}`,
      pagado: false,
    };
  }

  /*
    Sprint 137 — "(incluye envío)" describe el monto; "SE PAGA AL ENTREGAR" describe **el trato**,
    que es lo que el repartidor necesita saber antes de tocar la puerta.
  */
  return {
    principal: `COBRAR ${fmt(d.total)}`,
    secundario: d.pagaAlEntregar
      ? d.costoEnvio > 0
        ? 'SE PAGA AL ENTREGAR (incluye envío)'
        : 'SE PAGA AL ENTREGAR'
      : d.costoEnvio > 0
        ? '(incluye envío)'
        : 'AL ENTREGAR',
    pagado: false,
  };
}
